import { useEffect, useState } from "react";
import { Button, FormField } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import type { AssignmentRole, DealDetailDto } from "@/types/deals";
import { useAssignB1 } from "@/hooks/deals/useDeals";
import { useB1Picker } from "@/hooks/deals/useDealLookups";
import { dealLabel } from "./dealFormat";

interface Props {
  open: boolean;
  deal: DealDetailDto | null;
  /** Initial assignment, for B1 only: Send to Underwriting assigns A1
   *  (owner ruling, 2026-10-26). Reassignment lives in ReassignModal
   *  (ADMIN-only). */
  role: Extract<AssignmentRole, "B1">;
  onClose: () => void;
  onAssigned?: (deal: DealDetailDto) => void;
}

/**
 * Spec §6.2 — initial B1 assignment, from the Intake tab.
 * The picker is the same /api/users/assignable surface that powers
 * ReassignModal; reason is optional on first assignment (server contract
 * accepts null).
 */
export function AssignmentModal({ open, deal, role, onClose, onAssigned }: Props) {
  const toast = useToast();
  const [assigneeUserId, setAssigneeUserId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const usersQuery = useB1Picker();
  const mutation = useAssignB1(deal?.id ?? "");

  useEffect(() => {
    if (open) {
      setAssigneeUserId("");
      setReason("");
      setError(null);
    }
  }, [open]);

  if (!deal) return null;

  const candidates = usersQuery.data ?? [];

  async function submit() {
    if (!assigneeUserId) {
      setError(`Select a ${role}.`);
      return;
    }
    setError(null);
    try {
      const result = await mutation.mutateAsync({
        assigneeUserId,
        reason: reason.trim() || null,
      });
      toast.success(`Assigned ${role} to ${dealLabel(deal!)}.`);
      onAssigned?.(result);
      onClose();
    } catch (err) {
      const msg =
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Assignment failed.";
      setError(msg);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Assign ${role}`}
      description={`${deal.dealNumber} — ${dealLabel(deal)}`}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={mutation.isPending}>
            Assign
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <p className="text-small text-gray-mid">
          Assigning a B1 advances the deal into Pre-Approval. The B1 will receive the auto-spawned document request set.
        </p>

        <FormField label={`${role} assignee`} required>
          <Select
            value={assigneeUserId}
            onChange={(e) => setAssigneeUserId(e.target.value)}
            aria-label={`${role} assignee`}
            disabled={usersQuery.isLoading}
          >
            <option value="">
              {usersQuery.isLoading
                ? "Loading users…"
                : candidates.length === 0
                  ? `No ${role}s available`
                  : "Select an assignee…"}
            </option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField
          label="Note (optional)"
          helperText="Captured in the assignment history."
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why is this person the right fit?"
          />
        </FormField>

        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
